import { useToast } from '@chakra-ui/react';
import { ChangeEvent, createContext, ReactNode, useCallback, useContext } from 'react';
import { FormProvider, FormProviderProps } from 'react-hook-form';

type SimpleFormContextValues = {
  submitOnBlur?: boolean;
  simpleOnBlur: (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => Promise<void> | void;

  defaultValues?: Record<string, any>;
  updateField: (fieldName: string, value?: any) => Promise<void> | void;
  onValid: (fields: Record<string, any>) => Promise<any> | any;
  validateName: (name: string, value?: any) => Promise<Record<string, any> | undefined>;
  validateField: (name: string, value?: any) => Promise<Record<string, any> | undefined>;
  ensureDifferent: (name: string, value?: any) => Record<string, any> | undefined;
};

export const SimpleFormContext = createContext({} as SimpleFormContextValues);
export const useSimpleFormContext = () => useContext(SimpleFormContext);

type SimpleFormProps<T> = FormProviderProps<T> & {
  onValid?: (fields: Partial<T>) => Promise<any> | any;
  children: ReactNode;
  defaultValues?: Partial<T>;
  showToast?: boolean;
  submitOnBlur?: boolean;
  // NOTE: I honestly have no clue how our code works with this being how we do it, at least the types say we're doing this wrong.
  handleSubmit: any;
};

export const SimpleForm = <FormType,>({
  onValid,
  children,
  defaultValues,
  showToast = true,
  submitOnBlur = true,
  ...methods
}: SimpleFormProps<FormType>) => {
  const toast = useToast();

  const validateName = useCallback(
    // The any-s pain me, however I need to move on. They don't provide much protection anyway (coping)
    async (name: any, value?: any) => {
      const isValid = await methods.trigger(name);
      if (!isValid) return;

      value = value ?? methods.getValues(name);

      return { [name]: value } as Partial<FormType>;
    },
    [methods],
  );

  const ensureDifferent = useCallback(
    (key: string, value?: any) => {
      // TODO: manage comparing types better than loose comparisons
      const defaultValue = defaultValues?.[key];
      const isDifferent = defaultValue != value && !(defaultValue === null && value === '');

      if (!isDifferent) return;

      return { [key]: value } as Partial<FormType>;
    },
    [defaultValues],
  );

  const validateField = useCallback(
    async (key: string, value?: any) => {
      let field = await validateName(key, value);
      if (!field) return;

      field = ensureDifferent(key, field[key]);
      if (!field) return;

      return field;
    },
    [ensureDifferent, validateName],
  );

  const updateField = useCallback(
    async (fieldName: string, value?: any) => {
      const field = await validateField(fieldName, value);
      if (field?.[fieldName] === undefined) return;
      if (!onValid) return;

      try {
        await onValid?.(field);
        if (showToast) toast({ title: 'Successfully updated', status: 'success' });
      } catch (error) {
        toast({
          title: "Oops, that didn't work",
          status: 'error',
          description: error?.message,
        });
      }
    },
    [onValid, showToast, toast, validateField],
  );

  const simpleOnBlur = async (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
    if (!submitOnBlur) return;

    const fieldName = event.target.name;
    await updateField(fieldName);
  };

  return (
    <SimpleFormContext.Provider
      value={{
        simpleOnBlur,
        onValid,
        validateName,
        defaultValues,
        ensureDifferent,
        validateField,
        updateField,
        submitOnBlur,
      }}
    >
      <FormProvider {...methods}>{children}</FormProvider>
    </SimpleFormContext.Provider>
  );
};
